import React, { useState, useRef, useEffect } from 'react';
import WaveformVisualizer from './WaveformVisualizer';

const AudioRecorder = ({ onComplete, industryContext, isIndustryAnalysisEnabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcriptionResult, setTranscriptionResult] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [error, setError] = useState('');
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [savedTranscriptId, setSavedTranscriptId] = useState(null);
  
  // Manual transcript states
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [manualContent, setManualContent] = useState('');
  const [manualTitle, setManualTitle] = useState('');
  const [isManualSubmitting, setIsManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isReadingFile, setIsReadingFile] = useState(false);
  
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const streamRef = useRef(null);
  const recordingInterval = useRef(null);
  const statusPollingInterval = useRef(null);
  const fileInputRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        processAudio(audioBlob);
      };

      audioChunks.current = [];
      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setError('');
      
      // Start timing the recording
      const startTime = Date.now();
      setRecordingStartTime(startTime);
      
      // Update duration display every second
      recordingInterval.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        setRecordingDuration(elapsed);
      }, 100);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Microphone access required');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      
      // Stop timing
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
        recordingInterval.current = null;
      }
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder.current && isRecording) {
      // Stop the media recorder without triggering onstop event processing
      mediaRecorder.current.ondataavailable = null;
      mediaRecorder.current.onstop = null;
      mediaRecorder.current.stop();
      
      setIsRecording(false);
      setRecordingDuration(0);
      setRecordingStartTime(null);
      
      // Stop timing
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
        recordingInterval.current = null;
      }
      
      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        streamRef.current = null;
      }
      
      // Clear any accumulated audio chunks
      audioChunks.current = [];
      
      // Clear any previous audio blob and transcription
      setAudioBlob(null);
      setTranscriptionResult('');
      setError('');
      
      console.log('Recording cancelled by user');
    }
  };

  const processAudio = async (audioBlob) => {
    setIsTranscribing(true);
    setError('');

    try {
      const file = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
      
      // Create FormData for transcription API
      const formData = new FormData();
      formData.append('file', file);  // transcribe endpoint expects 'file' field

      const response = await fetch('/api/transcribe', {
        method: 'POST',
        body: formData,  // Send as FormData instead of raw file
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Transcription failed: ${response.status}`);
      }

      const result = await response.json();  // Parse JSON response
      const transcriptionResult = result.text || result.transcript || '';  // Extract text from response
      setTranscriptionResult(transcriptionResult);

      // Auto-save the transcript with enhanced metrics data
      await saveTranscript(transcriptionResult, audioBlob, result);

    } catch (err) {
      console.error('Transcription error:', err);
      setError('Transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const saveTranscript = async (content, audioFile, transcriptionData) => {
    try {
      const formData = new FormData();
      formData.append('title', `Recording ${new Date().toLocaleString()}`);
      formData.append('content', content);
      formData.append('audioFile', audioFile, 'recording.webm');
      
      // Include industry context for adaptive analysis
      if (isIndustryAnalysisEnabled && industryContext) {
        formData.append('industryContext', industryContext);
        formData.append('analysisType', 'industry');
      } else {
        formData.append('analysisType', 'generic');
      }
      
      // Include metrics data from transcription
      if (transcriptionData) {
        formData.append('transcriptionMetrics', JSON.stringify({
          audioDuration: recordingDuration || transcriptionData.duration || transcriptionData.audio_metrics?.estimated_duration_seconds || 0,
          detectedLanguage: transcriptionData.language,
          processingMetrics: transcriptionData.processing_metrics,
          audioMetrics: transcriptionData.audio_metrics,
          modelInfo: transcriptionData.model_info
        }));
      }

      const response = await fetch('/api/transcripts/save', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to save transcript: ${response.status}`);
      }

      const result = await response.json();
      
      // Start monitoring AI processing status
      if (result.transcript?.id) {
        setSavedTranscriptId(result.transcript.id);
        setIsProcessing(true);
        startStatusPolling(result.transcript.id);
      }

      // Notify parent component immediately (don't wait for AI processing)
      if (onComplete) {
        onComplete(content, result.transcript);
      }

    } catch (error) {
      console.error('Error saving transcript:', error);
      setError('Failed to save transcript');
    }
  };

  const startStatusPolling = (transcriptId) => {
    // Clear any existing polling
    if (statusPollingInterval.current) {
      clearInterval(statusPollingInterval.current);
    }

    let pollAttempts = 0;
    const maxPollAttempts = 60; // 5 minutes at 5-second intervals

    // Poll every 5 seconds for status updates (reduced from 2 seconds to avoid rate limits)
    statusPollingInterval.current = setInterval(async () => {
      try {
        pollAttempts++;
        
        const response = await fetch(`/api/transcripts/${transcriptId}/status`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setProcessingStatus(data);

          // Stop polling when processing is complete OR when we've reached max attempts
          if (data.completion?.isComplete || data.completion?.percentage >= 100) {
            setIsProcessing(false);
            clearInterval(statusPollingInterval.current);
            statusPollingInterval.current = null;
            console.log('🎉 AI processing completed for transcript:', transcriptId);
          } else if (pollAttempts >= maxPollAttempts) {
            // Timeout after max attempts
            setIsProcessing(false);
            clearInterval(statusPollingInterval.current);
            statusPollingInterval.current = null;
            console.log('⏰ Status polling timeout after max attempts for transcript:', transcriptId);
          }
        } else if (response.status === 429) {
          // Rate limit hit - stop polling temporarily
          console.warn('Rate limit hit, stopping status polling for transcript:', transcriptId);
          setIsProcessing(false);
          clearInterval(statusPollingInterval.current);
          statusPollingInterval.current = null;
        } else {
          console.warn('Failed to fetch processing status:', response.status, response.statusText);
        }
      } catch (error) {
        console.warn('Failed to fetch processing status:', error);
        
        // If we've tried many times and keep failing, stop polling
        if (pollAttempts >= 10) {
          setIsProcessing(false);
          clearInterval(statusPollingInterval.current);
          statusPollingInterval.current = null;
          console.log('❌ Stopping status polling due to repeated failures for transcript:', transcriptId);
        }
      }
    }, 5000); // Changed from 2000ms to 5000ms (5 seconds)

    // Stop polling after 5 minutes max (backup timeout)
    setTimeout(() => {
      if (statusPollingInterval.current) {
        clearInterval(statusPollingInterval.current);
        statusPollingInterval.current = null;
        setIsProcessing(false);
        console.log('⏰ Status polling timeout for transcript:', transcriptId);
      }
    }, 300000); // 5 minutes
  };

  const retryTranscription = () => {
    if (audioBlob) {
      processAudio(audioBlob);
    }
  };

  // Manual transcript functions
  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (limit to 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setManualError('File size must be less than 10MB');
        setSelectedFile(null);
        return;
      }

      // Check file type
      const allowedTypes = ['.txt', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (allowedTypes.includes(fileExtension)) {
        setSelectedFile(file);
        setManualError('');
        
        // Auto-populate title if empty
        if (!manualTitle.trim()) {
          const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
          setManualTitle(nameWithoutExtension);
        }
      } else {
        setManualError('Please select a .txt, .doc, or .docx file');
        setSelectedFile(null);
      }
    }
  };

  const readFileContent = async () => {
    if (!selectedFile) {
      setManualError('Please select a file first');
      return;
    }

    setIsReadingFile(true);
    setManualError('');

    try {
      let fileContent = '';
      
      if (selectedFile.name.toLowerCase().endsWith('.txt')) {
        // Handle .txt files
        fileContent = await readTextFile(selectedFile);
      } else if (selectedFile.name.toLowerCase().endsWith('.doc') || selectedFile.name.toLowerCase().endsWith('.docx')) {
        // Handle .doc/.docx files - for now, try to read as text
        try {
          fileContent = await readTextFile(selectedFile);
        } catch (error) {
          setManualError('Unable to read .doc/.docx file. Please save as .txt and try again, or copy and paste the content manually.');
          setIsReadingFile(false);
          return;
        }
      }

      // Populate the text area
      setManualContent(fileContent);
      setSelectedFile(null); // Clear selection after reading
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Clear file input
      }
      
    } catch (error) {
      console.error('Error reading file:', error);
      setManualError('Failed to read file content. Please try again or copy and paste manually.');
    } finally {
      setIsReadingFile(false);
    }
  };

  const readTextFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const result = event.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as text'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  };

  const handleManualSubmit = async () => {
    if (!manualContent.trim()) {
      setManualError('Please enter some text');
      return;
    }

    setIsManualSubmitting(true);
    setManualError('');

    try {
      const formData = new FormData();
      formData.append('title', manualTitle.trim() || `Manual Entry ${new Date().toLocaleString()}`);
      formData.append('content', manualContent.trim());
      
      // Include industry context for adaptive analysis
      if (isIndustryAnalysisEnabled && industryContext) {
        formData.append('industryContext', industryContext);
        formData.append('analysisType', 'industry');
      } else {
        formData.append('analysisType', 'generic');
      }
      
      // Add metadata to indicate this is a manual entry
      formData.append('transcriptionMetrics', JSON.stringify({
        isManualEntry: true,
        entryDate: new Date().toISOString(),
        contentLength: manualContent.trim().length
      }));

      const response = await fetch('/api/transcripts/save', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to save transcript: ${response.status}`);
      }

      const result = await response.json();

      // Notify parent component
      if (onComplete) {
        onComplete(manualContent.trim(), result.transcript);
      }

      // Reset form
      setManualContent('');
      setManualTitle('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setIsManualModalOpen(false);

    } catch (error) {
      console.error('Error saving manual transcript:', error);
      setManualError('Failed to save transcript');
    } finally {
      setIsManualSubmitting(false);
    }
  };

  const handleManualClose = () => {
    setIsManualModalOpen(false);
    setManualContent('');
    setManualTitle('');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setManualError('');
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Cleanup intervals on unmount
    return () => {
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
      }
      if (statusPollingInterval.current) {
        clearInterval(statusPollingInterval.current);
      }
    };
  }, []);

  // Status text helper
  const getStatusText = () => {
    if (isRecording) return `🔴 Recording ${formatDuration(recordingDuration)} - Stop to save or Cancel to discard`;
    if (isTranscribing) return 'Transcribing...';
    if (isProcessing && processingStatus) {
      return `Processing AI analysis... ${processingStatus.completion?.percentage || 0}%`;
    }
    if (transcriptionResult && !isProcessing) return 'Completed successfully';
    if (error) return error;
    return 'Ready to record';
  };

  const getStatusColor = () => {
    if (isRecording) return 'text-red-600';
    if (isTranscribing || isProcessing) return 'text-blue-600';
    if (transcriptionResult && !isProcessing) return 'text-green-600';
    if (error) return 'text-red-600';
    return 'text-gray-500';
  };

  const renderProcessingDetails = () => {
    if (!isProcessing || !processingStatus) return null;

    const { status } = processingStatus;
    const getStatusIcon = (taskStatus) => {
      return taskStatus === 'completed' ? '✅' : '⏳';
    };

    return (
      <div className="mt-2 p-2 bg-blue-50 rounded border text-xs">
        <div className="font-medium text-blue-800 mb-1">AI Processing Status:</div>
        <div className="grid grid-cols-2 gap-1 text-blue-700">
          <div>{getStatusIcon(status.embedding)} Embeddings</div>
          <div>{getStatusIcon(status.tagging)} Tags</div>
          <div>{getStatusIcon(status.sentiment)} Sentiment</div>
          <div>{getStatusIcon(status.entities)} Entities</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        {/* First Row: Record Button + Status + Manual Transcript Button */}
        <div className="flex items-center justify-between mb-2">
          {!isRecording ? (
            // Start Recording Button
            <button
              onClick={startRecording}
              disabled={isTranscribing}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-full p-2 transition-colors duration-200"
              title="Start Recording"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
              </svg>
            </button>
          ) : (
            // Recording Controls: Stop and Cancel
            <div className="flex items-center space-x-2">
              <button
                onClick={stopRecording}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors duration-200"
                title="Stop Recording & Save"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 012 0v6a1 1 0 11-2 0V7zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V7z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={cancelRecording}
                className="bg-gray-500 hover:bg-gray-600 text-white rounded-full p-2 transition-colors duration-200"
                title="Cancel Recording"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        
          <div className="flex-1 mx-3">
            <div className={`text-sm font-medium ${getStatusColor()}`}>
              {getStatusText()}
            </div>
            {error && (
              <button
                onClick={retryTranscription}
                className="text-xs text-blue-500 hover:text-blue-700 underline"
              >
                Retry
              </button>
            )}
          </div>

          {/* Manual Transcript Button */}
          <button
            onClick={() => setIsManualModalOpen(true)}
            disabled={isTranscribing || isManualSubmitting}
            className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white rounded-full p-2 transition-colors duration-200"
            title="Add Manual Transcript"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>
        </div>

        {/* Second Row: Visualizer */}
        <div className="bg-gray-50 rounded border border-gray-100">
          <WaveformVisualizer isRecording={isRecording} />
        </div>

        {/* Third Row: Processing Details (if processing) */}
        {renderProcessingDetails()}
      </div>

      {/* Manual Transcript Modal */}
      {isManualModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add Manual Transcript
              </h3>
              <button
                onClick={handleManualClose}
                className="text-gray-400 hover:text-gray-600 text-xl"
                disabled={isManualSubmitting}
              >
                ×
              </button>
            </div>

            {manualError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {manualError}
              </div>
            )}

            {/* Title Input */}
            <div className="mb-4">
              <label htmlFor="manualTitle" className="block text-sm font-medium text-gray-700 mb-2">
                Title (optional)
              </label>
              <input
                id="manualTitle"
                type="text"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
                placeholder="Enter a title for this transcript..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isManualSubmitting}
              />
            </div>

            {/* Content Text Area */}
            <div className="mb-6">
              <label htmlFor="manualContent" className="block text-sm font-medium text-gray-700 mb-2">
                Transcript Content *
              </label>
              <div className="text-xs text-gray-500 mb-2">
                Type or paste your transcript here, or use the "Select File" button below to upload a text file (.txt, .doc, .docx)
              </div>
              <textarea
                id="manualContent"
                value={manualContent}
                onChange={(e) => setManualContent(e.target.value)}
                placeholder="Type or paste your transcript here..."
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                disabled={isManualSubmitting || isReadingFile}
              />
              <div className="mt-1 text-sm text-gray-500">
                {manualContent.length} characters
              </div>
          </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              {/* File Upload Section - Left Side */}
              <div className="flex items-center space-x-2">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                
                {/* File Select Button */}
                <button
                  onClick={handleFileSelect}
                  disabled={isManualSubmitting || isReadingFile}
                  className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center"
                  title="Select a text file to upload"
                >
                  📁 Select File
                </button>
                
                {/* Read File Button */}
                <button
                  onClick={readFileContent}
                  disabled={!selectedFile || isManualSubmitting || isReadingFile}
                  className="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
                  title="Read the selected file and populate content"
                >
                  {isReadingFile ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-600 border-t-transparent mr-1"></div>
                      Reading...
                    </>
                  ) : (
                    '📖 Read File'
                  )}
                </button>
                
                {/* File Info */}
                {selectedFile && (
                  <span className="text-xs text-gray-500 max-w-[150px] truncate">
                    {selectedFile.name}
                  </span>
                )}
              </div>

              {/* Main Action Buttons - Right Side */}
              <div className="flex space-x-3">
                <button
                  onClick={handleManualClose}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isManualSubmitting || isReadingFile}
                >
                  Cancel
                </button>
                <button
                  onClick={handleManualSubmit}
                  disabled={isManualSubmitting || !manualContent.trim() || isReadingFile}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isManualSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Transcript'
                  )}
                </button>
              </div>
            </div>
      </div>
    </div>
      )}
    </>
  );
};

export default AudioRecorder; 